// React
import React, { useState, useContext, useRef, useEffect } from "react";

// Components
import { Col, Grid, Row } from "../../../../components/Grid";
import { Typography } from "../../../../components/Typography";
import { InputFieldWithLabel } from "../../../../components";
import { DropdownContainer, InviteCandidateFormContainer } from "./InviteCandidate.styled";
import { PrimaryButton } from "../../../../components/Button";
import { Icon } from "../../../../components";

// External imports
import { Formik, Field } from "formik";

// API
import { inviteApplicant } from "../../../../api/endpoints/applicant";
import ReactDropdown from "react-dropdown";

// State management
import { DispatchErrorContext } from "../../../../context/ErrorContext";
import { DispatchMessageContext } from "../../../../context/MessageContext";
import { StatePositionContext } from "../../../context/PositionContext";
import { useKanbanDispatch } from "../../../context/KanbanContext";
import { ApplicationParamsContext } from "../../../components/ApplicationList/ApplicationContextWrapper";

// Router and translations
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

const InviteCandidate = ({ onClose, openConfirmationModal }) => {
  const { t } = useTranslation();
  const errorDispatch = useContext(DispatchErrorContext);
  const messageDispatch = useContext(DispatchMessageContext);
  const { state, dispatch } = useContext(ApplicationParamsContext);
  const { refetchCompactApplications } = useKanbanDispatch();

  let { id } = useParams();

  //Formik external form handling
  const formRef = useRef();
  const handleSubmit = async () => {
    if (formRef.current) {
      const formikProps = formRef.current;
      await formikProps.submitForm();

      if (!formikProps.isSubmitting && formikProps.isValid) {
        try {
          const request = {
            body: {
              ...formikProps.values,
              positionId: positionId,
            },
          };
          const response = await inviteApplicant(request);
          if (response.success) {
            dispatch({
              type: "update_list",
              payload: [...state.list, response.data[0]],
            });
            formikProps.resetForm({});
            messageDispatch({
              type: "set_message",
              payload: t("message.succesful_invite"),
            });
            openConfirmationModal(); // Open the confirmation modal
            refetchCompactApplications(); // Refetch the compact applications
            onClose();
          } else {
            errorDispatch({
              type: "set_error",
              payload: response.data.data,
            });
          }
        } catch (err) {
          errorDispatch({
            type: "set_error",
            payload: JSON.stringify(err),
          });
        }
      }
    }
  };

  // Fetch positions and create dropdown
  const { positions } = useContext(StatePositionContext);
  const [activePositions, setActivePositions] = useState([]);
  const [defaultOption, setDefaultOption] = useState(null);
  const [positionId, setPositionId] = useState(null);

  useEffect(() => {
    // Filter active positions
    const activePositions = positions.filter((position) => position.status === "active");
    // Map positions to options for react-dropdown
    const options = activePositions.map((position) => ({
      value: position.id,
      label: position.title,
    }));
    setActivePositions(options);
    // Set default option to the first active position, if any
    if (options.length > 0) {
      const matchedOption = options.find((o) => String(o.value) === String(id));
      const selectedOption = matchedOption ?? options[0];
      setDefaultOption(selectedOption);
      setPositionId(selectedOption.value);
    }
  }, [positions, id]);

  const handleChange = (option) => {
    setPositionId(option.value);
  };

  return (
    <div>
      <Grid padding={32} fluid>
        <Typography tag="p" center>
          {t("common.inviteCandidateDescription")}
        </Typography>
        <DropdownContainer>
          <ReactDropdown options={activePositions} onChange={handleChange} value={defaultOption} />
          <Formik
            initialValues={{
              email: "",
              firstName: "",
              lastName: "",
            }}
            onSubmit={async (values, { resetForm }) => {
              try {
                const request = {
                  body: { ...values, positionId: positionId },
                };
                const response = await inviteApplicant(request);
                if (response.success) {
                  resetForm({});
                  messageDispatch({
                    type: "set_message",
                    payload: t("message.succesful_invite"),
                  });
                } else {
                  errorDispatch({
                    type: "set_error",
                    payload: response.data.data,
                  });
                }
              } catch (err) {
                errorDispatch({
                  type: "set_error",
                  payload: JSON.stringify(err),
                });
              }
            }}
            innerRef={formRef}
          >
            {(props) => {
              const { handleSubmit } = props;
              return (
                <InviteCandidateFormContainer>
                  <form onSubmit={handleSubmit}>
                    <Row smallerInnerGutter>
                      <Col>
                        <Field tiny label={false} as={InputFieldWithLabel} placeholder={t("common.first_name")} name="firstName" />
                      </Col>
                      <Col>
                        <Field tiny label={false} as={InputFieldWithLabel} placeholder={t("common.last_name")} name="lastName" />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Field as={InputFieldWithLabel} tiny label={false} placeholder={t("common.email")} name="email" />
                      </Col>
                    </Row>
                  </form>
                </InviteCandidateFormContainer>
              );
            }}
          </Formik>
          <PrimaryButton onClick={handleSubmit} red>
            {t("common.sendInvitation")}
            <Icon icon="send_message" mr="10" />
          </PrimaryButton>
          <PrimaryButton transparent mt="18" onClick={onClose}>
            {t("common.cancel")}
            <Icon icon="cross" mr="10" invert />
          </PrimaryButton>
        </DropdownContainer>
      </Grid>
    </div>
  );
};

export default InviteCandidate;
